import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import ErrorBoundary from './components/ErrorBoundary';
import PrivateRoute from './components/PrivateRoute';
import DashboardLayout from './components/DashboardLayout';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import RequestsListPage from './pages/RequestsListPage';
import CreateRequestPage from './pages/CreateRequestPage';
import RequestDetailPage from './pages/RequestDetailPage';
import StoresPage from './pages/StoresPage';
import SpaceTypesPage from './pages/SpaceTypesPage';
import InventoryPage from './pages/InventoryPage';
import AvailabilityBlocksPage from './pages/AvailabilityBlocksPage';
import ProvidersPage from './pages/ProvidersPage';
import StoreDependentsPage from './pages/admin/StoreDependentsPage';
import CredentialRequestsPage from './pages/CredentialRequestsPage';
import CreateCredentialRequestPage from './pages/CreateCredentialRequestPage';
import CredentialRequestDetailPage from './pages/CredentialRequestDetailPage';
import PromotionsPage from './pages/PromotionsPage';
import BadgesPage from './pages/BadgesPage';
import BenefitsPage from './pages/BenefitsPage';
import RedemptionsPage from './pages/RedemptionsPage';
import CommunicationsPage from './pages/CommunicationsPage';
import RankingPage from './pages/RankingPage';
import LoyaltyPortalPage from './pages/LoyaltyPortalPage';
import './App.css';

// Global error handler for uncaught errors (like Metamask)
window.addEventListener('error', (event) => {
  if (
    event.message?.includes('MetaMask') ||
    event.message?.includes('ethereum') ||
    event.message?.includes('web3') ||
    event.filename?.includes('chrome-extension')
  ) {
    event.preventDefault();
    console.warn('Ignored extension error:', event.message);
    return false;
  }
});

// Unhandled promise rejections
window.addEventListener('unhandledrejection', (event) => {
  if (
    event.reason?.message?.includes('MetaMask') ||
    event.reason?.message?.includes('ethereum') ||
    event.reason?.message?.includes('web3')
  ) {
    event.preventDefault();
    console.warn('Ignored extension promise rejection:', event.reason);
    return false;
  }
});

// Component to redirect based on user role
function RoleBasedRedirect() {
  const savedUser = localStorage.getItem('user');
  if (savedUser) {
    const user = JSON.parse(savedUser);
    if (user.role === 'PROVIDER') {
      return <Navigate to="/loyalty" replace />;
    }
  }
  return <Navigate to="/dashboard" replace />;
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/" element={<RoleBasedRedirect />} />
            <Route
              path="/"
              element={
                <PrivateRoute>
                  <DashboardLayout />
                </PrivateRoute>
              }
            >
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="requests" element={<RequestsListPage />} />
              <Route path="requests/create" element={<CreateRequestPage />} />
              <Route path="requests/:id" element={<RequestDetailPage />} />
              <Route path="stores" element={<PrivateRoute allowedRoles={['ADMIN']}><StoresPage /></PrivateRoute>} />
              <Route path="space-types" element={<PrivateRoute allowedRoles={['ADMIN']}><SpaceTypesPage /></PrivateRoute>} />
              <Route path="inventory" element={<PrivateRoute allowedRoles={['ADMIN', 'EXECUTIVE']}><InventoryPage /></PrivateRoute>} />
              <Route path="availability-blocks" element={<PrivateRoute allowedRoles={['ADMIN']}><AvailabilityBlocksPage /></PrivateRoute>} />
              <Route path="providers" element={<PrivateRoute allowedRoles={['ADMIN']}><ProvidersPage /></PrivateRoute>} />
              <Route path="store-dependents" element={<PrivateRoute allowedRoles={['ADMIN']}><StoreDependentsPage /></PrivateRoute>} />
              <Route path="credentials" element={<CredentialRequestsPage />} />
              <Route path="credentials/create" element={<CreateCredentialRequestPage />} />
              <Route path="credentials/:id" element={<CredentialRequestDetailPage />} />
              <Route path="promotions" element={<PromotionsPage />} />
              {/* Loyalty System Routes */}
              <Route path="badges" element={<PrivateRoute allowedRoles={['ADMIN']}><BadgesPage /></PrivateRoute>} />
              <Route path="benefits" element={<PrivateRoute allowedRoles={['ADMIN']}><BenefitsPage /></PrivateRoute>} />
              <Route path="redemptions" element={<PrivateRoute allowedRoles={['ADMIN']}><RedemptionsPage /></PrivateRoute>} />
              <Route path="communications" element={<PrivateRoute allowedRoles={['ADMIN']}><CommunicationsPage /></PrivateRoute>} />
              <Route path="ranking" element={<RankingPage />} />
              <Route path="loyalty" element={<PrivateRoute allowedRoles={['PROVIDER']}><LoyaltyPortalPage /></PrivateRoute>} />
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;